import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import PublicProfile from './pages/PublicProfile';
import ResetPassword from './pages/ResetPassword';
import MerchantBankLinked from './pages/MerchantBankLinked';
import MerchantCommissionPaid from './pages/MerchantCommissionPaid';
import ExtensionDownload from './pages/ExtensionDownload';
import Contact from './pages/Contact';
import About from './pages/About';
import HomeGate from './pages/Home';
import ShopGate from './pages/Shop';
import OfferDetailGate from './pages/OfferDetail';
import ShopOnlineGate from './pages/ShopOnline';
import SquadDetailGate from './pages/SquadDetail';
import SquadSettingsGate from './pages/SquadSettings';
import RewardsGate from './pages/Rewards';
import { FirstRewardGate, RewardItemGate, RewardPrizeGate } from './pages/RewardDetail';
import DreamGate from './pages/Dream';
import TravelGate from './pages/Travel';
import GamesGate from './pages/Games';
import ProfileGate from './pages/Profile';
import SettingsGate from './pages/Settings';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import OfficialRules from './pages/OfficialRules';
import apiService from './services/api';
import AppShell from './components/AppShell';
import TempStart from './pages/TempStart';
import './styles/App.css';

const ADMIN_ORIGIN = 'https://admin.gethomecrowd.com';

const ADMIN_PREFIXES = [
  '/staff',
  '/superuser',
  '/embed-tester',
  '/admin',
  '/onboarding',
  '/merchants',
  '/docs',
  '/early-access-overview',
  '/reward-selection',
  '/resources',
  '/preview',
];

function isAdminPath(pathname) {
  return ADMIN_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

function AdminPathRedirect() {
  const location = useLocation();

  useEffect(() => {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return;
    if (!isAdminPath(location.pathname)) return;
    window.location.replace(
      `${ADMIN_ORIGIN}${location.pathname}${location.search}${location.hash}`
    );
  }, [location]);

  return null;
}

function shellTitle(pathname) {
  const path = pathname.replace(/\/$/, '') || '/';
  if (path === '/') return 'Home';
  if (path.startsWith('/shop')) return 'Shop';
  if (path.startsWith('/rewards')) return 'Rewards';
  if (path.startsWith('/dream')) return 'Rewards';
  if (path.startsWith('/travel')) return 'Travel';
  if (path.startsWith('/games')) return 'Games';
  if (path.startsWith('/squads')) return 'Squad';
  if (path.startsWith('/profile')) return 'Profile';
  if (path.startsWith('/settings')) return 'Settings';
  return '';
}

function AppLayout() {
  const { pathname } = useLocation();
  if (!apiService.isAuthenticated()) {
    const path = pathname.replace(/\/$/, '') || '/';
    if (path === '/' || path === '/profile' || path === '/settings') {
      return <Navigate to="/login" replace />;
    }
    return <TempStart />;
  }
  return (
    <AppShell title={shellTitle(pathname)}>
      <Outlet />
    </AppShell>
  );
}

function App() {
  return (
    <Router>
      <div className="app">
        <AdminPathRedirect />
        <Routes>
          <Route path="/login/" element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup/" element={<Signup />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password/" element={<ForgotPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-email/" element={<VerifyEmail />} />
          <Route path="/mobile/verify-email" element={<VerifyEmail />} />
          <Route path="/mobile/verify-email/" element={<VerifyEmail />} />
          <Route path="/hc_prof/:fanId" element={<PublicProfile />} />
          <Route path="/hc_prof/:fanId/" element={<PublicProfile />} />
          <Route path="/mobile/u/:fanId" element={<PublicProfile />} />
          <Route path="/mobile/u/:fanId/" element={<PublicProfile />} />
          {/* The buttons of the fan emails point at the app links; without the app, these land on the web. */}
          <Route path="/mobile/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/mobile/dashboard/" element={<Navigate to="/" replace />} />
          <Route path="/mobile/marketplace" element={<Navigate to="/shop" replace />} />
          <Route path="/mobile/marketplace/" element={<Navigate to="/shop" replace />} />
          <Route path="/mobile/rewards" element={<Navigate to="/rewards" replace />} />
          <Route path="/mobile/rewards/" element={<Navigate to="/rewards" replace />} />
          <Route path="/mobile/reset-password" element={<ResetPassword />} />
          <Route path="/mobile/reset-password/" element={<ResetPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reset-password/" element={<ResetPassword />} />

          {/* Public: where Stripe returns a merchant after ACH bank setup. They
              have no Homecrowd account, so this must stay outside PrivateRoute. */}
          <Route path="/merchant/bank-linked" element={<MerchantBankLinked />} />
          <Route path="/merchant/bank-linked/" element={<MerchantBankLinked />} />
          <Route path="/merchant/commission-paid" element={<MerchantCommissionPaid />} />
          <Route path="/merchant/commission-paid/" element={<MerchantCommissionPaid />} />

          <Route path="/extension-download/" element={<ExtensionDownload />} />
          <Route path="/about/" element={<About />} />
          <Route path="/privacy-policy/" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions/" element={<TermsAndConditions />} />
          <Route path="/official-rules/" element={<OfficialRules />} />
          <Route path="/contact-us" element={<Contact />} />
          <Route path="/contact-us/" element={<Contact />} />
          <Route path="/support/" element={<Navigate to="/settings?tab=support" replace />} />
          <Route path="/support" element={<Navigate to="/settings?tab=support" replace />} />

          <Route element={<AppLayout />}>
            <Route path="/shop" element={<ShopGate />} />
            <Route path="/shop/" element={<ShopGate />} />
            <Route path="/shop/online" element={<ShopOnlineGate />} />
            <Route path="/shop/online/" element={<ShopOnlineGate />} />
            <Route path="/shop/offer/:id" element={<OfferDetailGate />} />
            <Route path="/rewards" element={<RewardsGate />} />
            <Route path="/rewards/" element={<RewardsGate />} />
            <Route path="/rewards/item/:id" element={<RewardItemGate />} />
            <Route path="/rewards/prize/:type" element={<RewardPrizeGate />} />
            <Route path="/rewards/first/:id" element={<FirstRewardGate />} />
            <Route path="/dream" element={<DreamGate />} />
            <Route path="/dream/" element={<DreamGate />} />
            <Route path="/dream/drops/:dropId" element={<DreamGate />} />
            <Route path="/travel" element={<TravelGate />} />
            <Route path="/travel/" element={<TravelGate />} />
            <Route path="/games" element={<GamesGate />} />
            <Route path="/games/" element={<GamesGate />} />
            <Route path="/squads/:id" element={<SquadDetailGate />} />
            <Route path="/squads/:id/" element={<SquadDetailGate />} />
            <Route path="/squads/:id/settings" element={<SquadSettingsGate />} />
            <Route path="/squads/:id/settings/" element={<SquadSettingsGate />} />
            <Route path="/profile" element={<ProfileGate />} />
            <Route path="/profile/" element={<ProfileGate />} />
            <Route path="/settings" element={<SettingsGate />} />
            <Route path="/settings/" element={<SettingsGate />} />
            <Route path="/" element={<HomeGate />} />
          </Route>
        </Routes>
      </div>
    </Router>
  );
}

export default App;
